import { PublicationGroupStatus, PublicationStatusEnum } from '@web/vin-sessions/types';

/**
 * Maps a publication status to the corresponding STIS Publications list-view tab
 * `filter` value. Both {@link PublicationStatusEnum} and
 * {@link PublicationGroupStatus} share the same string values, so either can be
 * passed.
 *
 * Used when navigating back to the list from the publication form so the user
 * returns to the tab they came from (Draft, Published, etc.) instead of the
 * default "All" tab.
 */
export const getListFilterForStatus = (
    status?: PublicationStatusEnum | PublicationGroupStatus | string | null
): string => {
    switch (status) {
        case PublicationStatusEnum.Draft:
            return 'draft';
        case PublicationStatusEnum.Published:
            return 'published';
        case PublicationStatusEnum.Archived:
            return 'archived';
        default:
            return 'all';
    }
};

/**
 * Builds the STIS Publications list path with the tab `filter` preselected for
 * the given status.
 */
export const getPublicationsListPath = (
    status?: PublicationStatusEnum | PublicationGroupStatus | string | null
): string => `/administration/publications?filter=${getListFilterForStatus(status)}`;

export const DEFAULT_ADMIN_LIST_SORT = 'updatedAt,desc';

/**
 * A sort the user picked is kept, and a text search is left alone because the backend ranks those by
 * best match.
 *
 * @param params the list query string
 * @param options whether this is an admin list, and the text search in the search row
 * @returns the query string the fetch should use
 */
export const withDefaultListSort = (
    params: string,
    { isAdminList, searchQuery }: { isAdminList: boolean; searchQuery?: string }
): string => {
    if (!isAdminList || searchQuery || new URLSearchParams(params).get('sort')) {
        return params;
    }
    return params.length > 0 ? `${params}&sort=${DEFAULT_ADMIN_LIST_SORT}` : `sort=${DEFAULT_ADMIN_LIST_SORT}`;
};
